import React from 'react';
import {
  Platform, TouchableOpacity, LayoutChangeEvent, NativeSyntheticEvent, NativeScrollEvent, FlatList as RNFlatList,
} from 'react-native';
import {
  Box, Center, Spinner, Icon, FlatList, RefreshControl,
} from '@gluestack-ui/themed';
import { BlurView } from 'expo-blur';
import { ChevronDown } from 'lucide-react-native';
import Animated, {
  useAnimatedStyle,
  useAnimatedReaction,
  runOnJS,
  withTiming,
  withRepeat,
  withSequence,
  interpolate,
  Extrapolation,
  SharedValue,
} from 'react-native-reanimated';

import { BackgroundImage } from './components/BackgroundImage';
import { TimelineItem } from './components/TimelineItem';
import { ModuleItem } from './components/ModuleItem';
import { ProgramArchitect } from './components/ProgramArchitect';
import {
  Module, TimelineItem as TimelineItemType, User,
} from '../types';

interface MainListItem {
  type: 'dashboard' | 'module';
  module?: Module;
  id: string;
}

interface CourseScreenViewProps {
  isLoading: boolean;
  refreshing: boolean;
  user: User;
  pageHeight: number;
  pageHeightSV: SharedValue<number>;
  scrollY: SharedValue<number>;
  sortedModules: Module[];
  timelineItems: TimelineItemType[];
  mainListData: MainListItem[];
  flatListRef: React.RefObject<RNFlatList<MainListItem>>;
  timelineRef: React.RefObject<RNFlatList<TimelineItemType>>;
  selectedModule?: { moduleId: string; courseId: string };
  onScroll: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  onRefresh: () => void;
  handleLayout: (event: LayoutChangeEvent) => void;
  handleTimelinePress: (index: number) => void;
  handleScrollDown: () => void;
  handleOpenModule: (params: { moduleId: string; courseId?: string }) => void;
  syncTimelineScroll: (index: number) => void;
}

const AnimatedBox = Animated.createAnimatedComponent(Box);

const TIMELINE_CONTENT_CONTAINER_STYLE = {
  paddingHorizontal: 24,
  alignItems: 'center' as const,
  height: '100%' as const,
};

/**
 * CourseScreenView is the presentation component for the CourseScreen.
 * It manages the layout of the immersive background, sticky timeline, and main scrollable content.
 */
export const CourseScreenView: React.FC<CourseScreenViewProps> = ({
  isLoading,
  refreshing,
  user,
  pageHeight,
  pageHeightSV,
  scrollY,
  sortedModules,
  timelineItems,
  mainListData,
  flatListRef,
  timelineRef,
  selectedModule,
  onScroll,
  onRefresh,
  handleLayout,
  handleTimelinePress,
  handleScrollDown,
  handleOpenModule,
  syncTimelineScroll,
}) => {
  const arrowAnimatedStyle = useAnimatedStyle(() => {
    const height = pageHeightSV.value;
    const isAtEnd = scrollY.value >= (mainListData.length - 1) * height - 10;
    return {
      opacity: withTiming(isAtEnd || mainListData.length <= 1 ? 0 : 1),
      transform: [
        { translateY: withTiming(isAtEnd ? 20 : 0) },
        {
          translateY: withRepeat(
            withSequence(
              withTiming(5, { duration: 800 }),
              withTiming(0, { duration: 800 }),
            ),
            -1,
            true,
          ),
        },
      ],
    };
  });

  const dashboardBgStyle = useAnimatedStyle(() => {
    const height = Math.max(pageHeightSV.value, 100);
    return {
      opacity: interpolate(scrollY.value, [0, height], [1, 0], Extrapolation.CLAMP),
    };
  });

  useAnimatedReaction(
    () => {
      const height = pageHeightSV.value;
      if (height <= 0) return 0;
      return Math.round(scrollY.value / height);
    },
    (activeIndex, previousIndex) => {
      if (activeIndex !== previousIndex && activeIndex >= 0 && activeIndex < mainListData.length) {
        runOnJS(syncTimelineScroll)(activeIndex);
      }
    },
    [mainListData.length, syncTimelineScroll],
  );

  if (isLoading) {
    return (
      <Center testID="loading-spinner" w="100%" h="100%" bg="$bgApp">
        <Spinner size="large" />
      </Center>
    );
  }

  return (
    <Box flex={1} onLayout={handleLayout} testID="course-screen-new">
      {/* Immersive Background Images */}
      <AnimatedBox
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="$bgApp"
        style={dashboardBgStyle}
      />
      {sortedModules.map((module, index) => (
        <BackgroundImage
          key={`bg-${module.id}`}
          module={module}
          index={index + 1}
          scrollY={scrollY}
          pageHeightSV={pageHeightSV}
        />
      ))}

      {/* Sticky Timeline Header */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        zIndex={100}
        bg="$bgApp"
        h={80}
        borderBottomWidth={1}
        borderColor="$borderLight"
        testID="timeline-header"
        pointerEvents="box-none"
      >
        <FlatList
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ref={timelineRef as any}
          horizontal
          data={timelineItems}
          showsHorizontalScrollIndicator={false}
          keyExtractor={(item: TimelineItemType) => `timeline-${item.type}-${item.id}`}
          flex={1}
          contentContainerStyle={TIMELINE_CONTENT_CONTAINER_STYLE}
          onScrollToIndexFailed={(info) => {
            setTimeout(() => {
              timelineRef.current?.scrollToIndex({ index: info.index, animated: true });
            }, 500);
          }}
          renderItem={({ item, index }) => (
            <TimelineItem
              item={item as TimelineItemType}
              index={index}
              scrollY={scrollY}
              onPress={() => handleTimelinePress(index)}
              pageHeightSV={pageHeightSV}
            />
          )}
        />
      </Box>

      {/* Main Content Vertical List */}
      <Animated.FlatList
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={flatListRef as any}
        testID="main-module-list"
        data={mainListData}
        extraData={pageHeight}
        keyExtractor={(item: MainListItem) => `main-${item.type}-${item.id}`}
        onScroll={onScroll}
        pagingEnabled
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        getItemLayout={(_, index) => ({
          length: pageHeight,
          offset: pageHeight * index,
          index,
        })}
        onScrollToIndexFailed={(info) => {
          setTimeout(() => {
            flatListRef.current?.scrollToIndex({ index: info.index, animated: true });
          }, 500);
        }}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        renderItem={({ item, index }) => {
          if (item.type === 'dashboard') {
            return (
              <ProgramArchitect
                modules={sortedModules}
                onSelectModule={handleTimelinePress}
                onOpenModule={handleOpenModule}
                user={user}
                pageHeight={pageHeight}
              />
            );
          }
          return (
            <ModuleItem
              item={item.module!}
              index={index}
              scrollY={scrollY}
              handleOpenModule={handleOpenModule}
              pageHeightSV={pageHeightSV}
              pageHeight={pageHeight}
              selectedModule={selectedModule}
            />
          );
        }}
        snapToInterval={pageHeight}
        snapToAlignment="start"
        decelerationRate="fast"
        disableIntervalMomentum
      />

      {/* Elegant Scroll-Down Arrow */}
      <Box
        position="absolute"
        bottom={40}
        alignSelf="center"
        zIndex={100}
      >
        <Animated.View style={arrowAnimatedStyle}>
          <TouchableOpacity onPress={handleScrollDown} activeOpacity={0.7}>
            {Platform.OS !== 'web' ? (
              <Box borderRadius="$full" overflow="hidden" softShadow="1">
                <BlurView intensity={20} tint="light">
                  <Box p="$3">
                    <Icon as={ChevronDown} size="xl" color="$brandBase" />
                  </Box>
                </BlurView>
              </Box>
            ) : (
              <Box bg="$white" p="$3" borderRadius="$full" softShadow="1" opacity={0.8}>
                <Icon as={ChevronDown} size="xl" color="$brandBase" />
              </Box>
            )}
          </TouchableOpacity>
        </Animated.View>
      </Box>
    </Box>
  );
};
